import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  viewPlaylist,
  savePlaylist,
  removePlaylistItem,
} from "../../functions/playlist";
import "./style.css";

export default function PlaylistViewer({
  playlist,
  onPlaylistUpdate,
  isOwner = false,
}) {
  const { user } = useSelector((state) => ({ ...state }));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSaved, setIsSaved] = useState(
    playlist.savedBy?.includes(user.id) || false
  );
  const [saving, setSaving] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    // Registrar visualização
    const registerView = async () => {
      await viewPlaylist(playlist._id, user.token);
    };
    registerView();
  }, [playlist._id, user.token]);

  const getCurrentItem = () => {
    const items = playlist.items || [];
    return items[currentIndex];
  };

  const getVideoUrl = (item) => {
    if (!item) return null;

    if (item.itemType === "video" && item.postRef) {
      return item.postRef.videos?.[0]?.url;
    }
    if (item.itemType === "live" && item.liveRef) {
      return item.liveRef.recording?.url;
    }
    if (item.itemType === "story" && item.storyRef) {
      return item.storyRef.media?.url;
    }
    return null;
  };

  const getThumbnail = (item) => {
    if (!item) return null;

    if (item.itemType === "video" && item.postRef) {
      return item.postRef.images?.[0]?.url || item.postRef.videos?.[0]?.url;
    }
    if (item.itemType === "live" && item.liveRef) {
      return item.liveRef.thumbnail;
    }
    if (item.itemType === "story" && item.storyRef) {
      return item.storyRef.media?.url;
    }
    return null;
  };

  const getItemTitle = (item) => {
    if (!item) return "Sem título";

    if (item.itemType === "video" && item.postRef) {
      return item.postRef.text?.substring(0, 50) || "Vídeo";
    }
    if (item.itemType === "live" && item.liveRef) {
      return item.liveRef.title || "Live gravada";
    }
    if (item.itemType === "story" && item.storyRef) {
      return item.storyRef.text || "Story";
    }
    return "Conteúdo";
  };

  const getItemUser = (item) => {
    if (!item) return null;

    if (item.itemType === "video" && item.postRef?.user) {
      return item.postRef.user;
    }
    if (item.itemType === "live" && item.liveRef?.host) {
      return item.liveRef.host;
    }
    if (item.itemType === "story" && item.storyRef?.user) {
      return item.storyRef.user;
    }
    return null;
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);

    const res = await savePlaylist(playlist._id, user.token);
    if (res.status === "ok") {
      setIsSaved(res.data.saved);
    }
    setSaving(false);
  };

  const handleRemoveItem = async (itemId) => {
    if (!isOwner) return;

    const res = await removePlaylistItem(playlist._id, itemId, user.token);
    if (res.status === "ok" && onPlaylistUpdate) {
      onPlaylistUpdate();
    }
  };

  const handleVideoEnded = () => {
    // Auto-play próximo vídeo
    if (currentIndex < (playlist.items?.length || 0) - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const currentItem = getCurrentItem();
  const videoUrl = getVideoUrl(currentItem);

  return (
    <div className="playlist_viewer">
      <div className="playlist_viewer_header">
        <div className="playlist_viewer_info">
          <div className="playlist_viewer_title">{playlist.title}</div>
          <div className="playlist_viewer_meta">
            <Link to={`/profile/${playlist.user?.username}`}>
              {playlist.user?.first_name} {playlist.user?.last_name}
            </Link>
            <span>•</span>
            <span>{playlist.items?.length || 0} vídeos</span>
            <span>•</span>
            <span>{playlist.viewCount || 0} visualizações</span>
          </div>
        </div>
        <div className="playlist_viewer_actions">
          {!isOwner && (
            <button
              className={`playlist_action_btn ${isSaved ? "saved" : ""}`}
              onClick={handleSave}
              disabled={saving}
              title={isSaved ? "Remover dos salvos" : "Salvar playlist"}
            >
              <svg viewBox="0 0 24 24">
                <path
                  d={
                    isSaved
                      ? "M17 3H7c-1.1 0-1.99.9-1.99 2L5 21l7-3 7 3V5c0-1.1-.9-2-2-2z"
                      : "M17 3H7c-1.1 0-1.99.9-1.99 2L5 21l7-3 7 3V5c0-1.1-.9-2-2-2zm0 15l-5-2.18L7 18V5h10v13z"
                  }
                />
              </svg>
            </button>
          )}
          <button className="playlist_action_btn" title="Compartilhar">
            <svg viewBox="0 0 24 24">
              <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" />
            </svg>
          </button>
        </div>
      </div>

      <div className="playlist_player">
        {videoUrl ? (
          <video
            ref={videoRef}
            src={videoUrl}
            controls
            autoPlay
            onEnded={handleVideoEnded}
          />
        ) : (
          <div className="playlist_player_empty">
            <svg viewBox="0 0 24 24">
              <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8 12.5v-9l6 4.5-6 4.5z" />
            </svg>
            <span>Nenhum vídeo disponível</span>
          </div>
        )}
      </div>

      <div className="playlist_items">
        {(playlist.items || []).map((item, index) => (
          <div
            key={item._id}
            className={`playlist_item ${index === currentIndex ? "active" : ""}`}
            onClick={() => setCurrentIndex(index)}
          >
            <div className="playlist_item_index">{index + 1}</div>
            <div className="playlist_item_thumbnail">
              {item.itemType === "video" ? (
                <video src={getThumbnail(item)} muted />
              ) : (
                <img src={getThumbnail(item)} alt="" />
              )}
            </div>
            <div className="playlist_item_info">
              <div className="playlist_item_title">{getItemTitle(item)}</div>
              <div className="playlist_item_meta">
                {getItemUser(item) && (
                  <span>
                    {getItemUser(item).first_name} {getItemUser(item).last_name}
                  </span>
                )}
                <span className="playlist_item_type">{item.itemType}</span>
              </div>
            </div>
            {isOwner && (
              <button
                className="playlist_action_btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveItem(item._id);
                }}
                title="Remover"
              >
                <svg viewBox="0 0 24 24">
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                </svg>
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
